// iter264 — Dark mode scaffold. Keep this file tiny and dependency-free.
//
// Contract:
//   - "abs_theme" localStorage key holds "light" | "dark".
//   - When missing, we fall back to the OS `prefers-color-scheme`.
//   - `applyTheme(t)` toggles the `dark` class on <html> so shadcn + Tailwind's
//     dark: variants light up consistently.
//   - Never crash in SSR / non-window contexts (defensive guards).

const STORAGE_KEY = "abs_theme";

export function getStoredTheme() {
  try {
    return typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
  } catch {
    return null;
  }
}

export function getSystemTheme() {
  try {
    if (typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
  } catch {
    // ignore — Safari private mode / SSR
  }
  return "light";
}

export function resolveInitialTheme() {
  const stored = getStoredTheme();
  if (stored === "light" || stored === "dark") return stored;
  // iter266 — default new / first-time visitors to dark mode (per user
  // request post-iter265). Users who explicitly toggle to light still
  // have their choice persisted; the OS preference is intentionally
  // ignored here so the brand experience is consistent across devices.
  return "dark";
}

export function applyTheme(theme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add("dark");
    root.style.colorScheme = "dark";
  } else {
    root.classList.remove("dark");
    root.style.colorScheme = "light";
  }
}

export function setStoredTheme(theme) {
  try {
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // ignore — storage disabled
  }
}

export function toggleTheme(current) {
  const next = current === "dark" ? "light" : "dark";
  setTheme(next);
  return next;
}

// Apply + persist + notify (Sidebar label, etc.).
export function setTheme(theme) {
  applyTheme(theme);
  setStoredTheme(theme);
  try { window.dispatchEvent(new CustomEvent("abs-theme", { detail: theme })); } catch { /* ignore */ }
}

export function subscribeTheme(fn) {
  const h = (e) => fn(e.detail);
  window.addEventListener("abs-theme", h);
  return () => window.removeEventListener("abs-theme", h);
}
